// src/pages/DebtStudio/components/DealForm/IssuerSelect.jsx
import React, { useEffect, useState, useRef } from "react";
import { supabase } from "services/supabaseClient";
import { useDebtStudio } from "pages/DebtStudio/context/DebtStudioContext";
import { TextInput } from "components/InputStyled";
import { findExactCanonicalIssuer } from "pages/DebtStudio/lib/issuerIdentity";

export default function IssuerSelect({ isReadOnly: isReadOnlyProp } = {}) {
  const { form, setForm, isReadOnly: ctxReadOnly } = useDebtStudio();
  const isReadOnly = isReadOnlyProp ?? ctxReadOnly;

  const [query, setQuery] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const containerRef = useRef(null);

  /* ------------------------------------------------------------------
   * Hydrate issuer display
   * Runs ONLY when a deal is loaded or issuerId changes
   * ------------------------------------------------------------------ */
  useEffect(() => {
    let cancelled = false;

    async function hydrateIssuer() {
      setIsEditing(false);
      setOpen(false);
      setResults([]);

      // Canonical issuer
      if (form?.issuerId) {
        const { data } = await supabase
          .from("issuers")
          .select("legal_name")
          .eq("id", form.issuerId)
          .maybeSingle();

        if (!cancelled && data?.legal_name) {
          setQuery(data.legal_name);
        }
        return;
      }

      // Custom issuer
      const raw = form?.issuerNameRaw || "";
      setQuery(raw);
    }

    hydrateIssuer();
    return () => {
      cancelled = true;
    };
  }, [form?.id, form?.issuerId]);

  /* ------------------------------------------------------------------
   * Debounced RPC search (EDIT MODE ONLY)
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (isReadOnly) {
      setResults([]);
      setOpen(false);
      return;
    }

    if (!isEditing || !query || query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);

      const { data, error } = await supabase.rpc("search_issuers", {
        q: query,
        max_results: 10,
      });

      if (cancelled) return;

      if (!error && Array.isArray(data)) {
        const exactIssuer = findExactCanonicalIssuer(data, query);
        if (exactIssuer) {
          setForm((prev) => {
            if (prev?.issuerId || prev?.issuerNameRaw !== query) return prev;
            return {
              ...prev,
              issuerId: exactIssuer.id,
              issuerNameRaw: exactIssuer.legal_name,
              issuerStatus: "canonical",
            };
          });
          setIsEditing(false);
          setResults([]);
          setOpen(false);
        } else {
          setResults(data);
          setOpen(true);
        }
      } else {
        setResults([]);
        setOpen(false);
      }

      setLoading(false);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, isEditing, isReadOnly, setForm]);

  /* ------------------------------------------------------------------
   * Close dropdown on outside click
   * ------------------------------------------------------------------ */
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ------------------------------------------------------------------
   * Handlers
   * ------------------------------------------------------------------ */
  const handleSelect = (issuer) => {
    if (isReadOnly) return;

    setIsEditing(false);
    setQuery(issuer.legal_name);

    setForm((prev) => ({
      ...prev,
      issuerId: issuer.id,
      issuerNameRaw: issuer.legal_name,
      issuerStatus: "canonical",
    }));

    setResults([]);
    setOpen(false);
  };

  const handleChange = (v) => {
    if (isReadOnly) return;

    setIsEditing(true);
    setQuery(v);

    setForm((prev) => ({
      ...prev,
      issuerId: null,
      issuerNameRaw: v,
      issuerStatus: "custom",
    }));
  };

  /* ------------------------------------------------------------------
   * Render
   * ------------------------------------------------------------------ */
  return (
    <div ref={containerRef} className="relative">
      <TextInput
        label="Issuer"
        value={query}
        onChange={handleChange}
        placeholder="Search or enter issuer name"
        className="w-full"
        disabled={isReadOnly}
      />

      {!isReadOnly && loading && (
        <div className="absolute right-3 top-9 text-xs text-muted">
          Searching…
        </div>
      )}

      {!isReadOnly && open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-app shadow-sm max-h-64 overflow-y-auto">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              className="w-full text-left px-3 py-2 hover:bg-muted"
              onClick={() => handleSelect(r)}
            >
              <div className="font-medium">{r.legal_name}</div>
              {(r.state || r.issuer_type) && (
                <div className="text-xs text-muted">
                  {r.state}
                  {r.state && r.issuer_type ? " • " : ""}
                  {r.issuer_type}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
